import { useEffect, useState, useRef, useMemo, useCallback } from 'react';

import { getRoutesDirections } from '@/data/libraries/Routes';

import { useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';

import { isEmpty } from 'lodash';

import { RootState } from '@/ui/store/store';
import { useAppSelector } from '@/ui/store/helperRedux';

import CartController from '@/controllers/CartController';

import Icon from '@/ui/assets/Icon';
import { ICart, ICartResponse } from '@/domain/interfaces/ICart';

/**
 * Componente SVG a carrito de compras
 * @component
 */
const CartWidget = () => {
  /** Hook para trabajar con document location  */
  const location: Location = useLocation();

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /** Controlador de carrito */
  const { productQuantity, validateCart } = CartController();
  /** Estado para controlar si hay errores en el carrito */
  const [errorCart, setErrorCart] = useState<boolean>(false);
  /** Estado del carrito */
  const cartProducts = useAppSelector((state: RootState) => state.cartState);

  /** State del contador del componente */
  const [counter, setCounter] = useState<number>(0);

  /** Usamos una referencia para prevenir demasiadas validaciones */
  const validateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /** Flag para identificar si el carrito está vacío */
  const isCartEmpty = useMemo(() => {
    if (isEmpty(cartProducts)) return true;
    const cart = cartProducts as ICart;
    return !cart.details || cart.details.length === 0;
  }, [cartProducts]);

  /** Memoizamos la función de validación para evitar recrearla en cada renderizado */
  const checkCartErrors = useCallback(() => {
    if (isCartEmpty) {
      setErrorCart(false);
      return;
    }

    try {
      const responseValidation: ICartResponse = validateCart();
      setErrorCart(responseValidation.error);
    } catch (error) {

      setErrorCart(false);
    }
  }, [validateCart, isCartEmpty]);

  /** Efecto separado solo para el contador */
  useEffect((): void => {
    /** Solo actualizar el contador si hay productos */
    if (!isCartEmpty) {
      setCounter(productQuantity());
    } else {
      setCounter(0);
      setErrorCart(false);
    }
  }, [productQuantity, isCartEmpty]);

  /** Efecto separado para validación con debounce para reducir llamadas */
  useEffect(() => {
    /** Si el carrito está vacío, no hacer nada */
    if (isCartEmpty) {
      setErrorCart(false);
      return;
    }

    /** Limpiamos el timeout si existe */
    if (validateTimeoutRef.current) {
      clearTimeout(validateTimeoutRef.current);
    }

    /** Creamos un nuevo timeout para limitar las peticiones */
    const timeoutId = setTimeout(() => {
      checkCartErrors();
    }, 500); /** Debounce de 500ms */

    validateTimeoutRef.current = timeoutId;

    /** Limpieza al desmontar */
    return () => {
      if (validateTimeoutRef.current) {
        clearTimeout(validateTimeoutRef.current);
        validateTimeoutRef.current = null;
      }
    };
  }, [cartProducts, checkCartErrors, isCartEmpty]);

  /** Efecto para detectar cambios en localStorage y forzar actualización */
  useEffect(() => {
    /** Usamos los cambios en cartProducts directamente en lugar de depender de eventos de storage */
    /** Cuando cartProducts cambia (por eliminación o actualización), actualizamos la UI */
    if (isEmpty(cartProducts)) {
      setCounter(0);
      setErrorCart(false);

      if (validateTimeoutRef.current) {
        clearTimeout(validateTimeoutRef.current);
        validateTimeoutRef.current = null;
      }
    }
  }, [cartProducts]);

  /** Renderiza el contador del carrito */
  const renderCounter = () => (
    <span className='position-absolute top-0 start-100 translate-middle-60-30 badge rounded-pill bg-danger p-small  '>{counter}</span>
  );

  /** Renderiza el icono de advertencia */
  const renderWarningIcon = () => (
    <span className='position-absolute top-100 translate-middle badge rounded-circle bg-complementary-4 small-circle  '>
      <Icon name='ico-warning' classMain='svg-12' />
    </span>
  );

  /** Renderiza el icono de carrito de compras */
  return (
    <div className='position-relative '>
      <Icon name='ico-market-car' />
      {location.pathname === routesDirections.CHECK_OUT_ROUTE || isCartEmpty || !errorCart ? renderCounter() : renderWarningIcon()}
    </div>
  );
};

export default CartWidget;
